import { useQuery } from '@tanstack/react-query';
import React, { useMemo } from 'react';
import { getTerminals } from 'Services/Api/_HumanWritten/PaymentService';
import { RadioInput } from 'Views/Components/New/RadioInput';
import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { store } from 'Models/Store';
import { NewModal } from 'Views/Components/Modal/NewModal';
import { Colors, Display } from 'Views/Components/Button/Button';
import classNames from 'classnames';
import If from 'Views/Components/If/If';
import Spinner from 'Views/Components/Spinner/Spinner';
import { useIntl } from 'react-intl';

export interface TerminalStore {
	terminal: string;
	selectTerminal: (terminal: string) => void;
}

export const useTerminalStore = create<TerminalStore, [['zustand/persist', unknown]]>(
	persist(
		(set, get) => ({
			terminal: '',
			selectTerminal: (terminal: string) => set({ terminal: terminal }),
		}),
		{
			name: 'selected-terminal',
		},
	),
);

interface TerminalPaymentProps {
	onConfirm: (terminal: string) => void;
	onCancel: () => void;
	onClose: () => void;
}

export const TerminalPayment = ({ onConfirm, onCancel, onClose }: TerminalPaymentProps) => {
	const intl = useIntl();
	const { terminal, selectTerminal } = useTerminalStore();

	const {
		data: terminals,
		isLoading,
		isSuccess,
	} = useQuery({
		queryKey: ['terminals'],
		queryFn: async () => {
			const { data } = await getTerminals();
			return data;
		},
	});

	const options = useMemo(() => {
		return (
			terminals?.map(t => ({
				label: t.label,
				value: t.id,
			})) ?? []
		);
	}, [terminals]);

	if (!isLoading && options.length === 0) {
		return <></>;
	}

	return (
		<>
			<NewModal.Header>
				<NewModal.Title>
					{intl.formatMessage({
						id: 'pos__payment-option__send',
						defaultMessage: 'Send to POS Device',
					})}
				</NewModal.Title>
			</NewModal.Header>
			<NewModal.Body>
				<If condition={isLoading}>
					<Spinner />
				</If>
				{!isLoading && isSuccess && options.length > 0 && (
					<RadioInput options={options} value={terminal} onChange={selectTerminal} />
				)}
			</NewModal.Body>
			<NewModal.Footer>
				<NewModal.CancelButton onClick={onCancel} display={Display.Outline} colors={Colors.Grey}>
					{intl.formatMessage({ id: 'general__cancel', defaultMessage: 'Cancel' })}
				</NewModal.CancelButton>
				<NewModal.ConfirmButton
					onClick={() => onConfirm(terminal)}
					display={Display.Solid}
					colors={Colors.Secondary}
					disabled={!terminal}
				>
					{intl.formatMessage({ id: 'general__confirm', defaultMessage: 'Confirm' })}
				</NewModal.ConfirmButton>
			</NewModal.Footer>
		</>
	);
};

export function terminalPaymentModal() {
	return new Promise<string>((resolve, reject) => {
		const onConfirm = async (terminal: string) => {
			store.modal.hide();
			resolve(terminal);
		};

		const onCancel = () => {
			store.modal.hide();
			reject();
		};

		const onClose = () => {
			store.modal.hide();
			reject();
		};

		store.modal?.show(
			'Send to Reader',
			<TerminalPayment onConfirm={onConfirm} onCancel={onCancel} onClose={onClose} />,
			{
				className: classNames('confirm-modal'),
				shouldCloseOnEsc: true,
				shouldCloseOnOverlayClick: true,
				onRequestClose: onCancel,
				newModal: true,
			},
		);
	});
}
